import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, Code2, Edit2, Eye, Send, X } from 'lucide-react';

const TEMPLATE_LIBRARY = [
  { key: 'classic', name: 'Classic Gradient', accent: '#10b981', surface: '#f9fafb' },
  { key: 'minimal', name: 'Minimal Clean', accent: '#0ea5e9', surface: '#ffffff' },
  { key: 'midnight', name: 'Midnight Bold', accent: '#22d3ee', surface: '#0f172a' }
];

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function formatMoney(value) {
  const num = Number(value || 0);
  return Number.isFinite(num) ? `$${num.toFixed(2)}` : '$0.00';
}

function createBuilderDefaults(type, context, fallback = {}) {
  const name = context.clientName || fallback.clientName || 'there';
  if (type === 'invoice') {
    return {
      greetingName: name,
      headline: 'Invoice ready',
      intro: `Your invoice ${context.invoiceNumber || ''} is ready for review.`,
      primaryCtaLabel: context.paymentUrl ? 'Pay Now' : 'View Invoice',
      secondaryCtaLabel: context.paymentUrl ? 'View Invoice' : '',
      footerNote: 'Thank you for your business.',
      showSecondary: Boolean(context.paymentUrl)
    };
  }
  if (type === 'proposal') {
    return {
      greetingName: name,
      headline: 'Proposal ready',
      intro: `We prepared proposal ${context.proposalNumber || ''} for your review.`,
      primaryCtaLabel: 'View Proposal',
      acceptLabel: 'Accept Proposal',
      rejectLabel: 'Decline Proposal',
      footerNote: 'Reply if you would like any adjustments.',
      showResponseButtons: Boolean(context.acceptLink || context.rejectLink)
    };
  }
  if (type === 'password-reset') {
    return {
      greetingName: name,
      headline: 'Reset your password',
      intro: 'Use the secure link below to reset your password.',
      primaryCtaLabel: 'Reset Password',
      footerNote: 'If you did not request this, ignore this email.'
    };
  }
  if (type === 'review-request') {
    return {
      greetingName: name,
      headline: 'Review request',
      intro: `Your project ${context.projectName || ''} is complete. We would appreciate your feedback.`,
      primaryCtaLabel: 'Leave Review',
      footerNote: 'Thank you for helping us improve.'
    };
  }
  return {
    greetingName: name,
    headline: 'You are invited',
    intro: 'Use the link below to access your portal.',
    primaryCtaLabel: 'Open Portal',
    footerNote: 'If the button does not work, copy and paste the link.'
  };
}

function buildSummaryRows(type, context) {
  if (type === 'invoice') {
    return [
      ['Invoice #', context.invoiceNumber || 'N/A'],
      ['Amount', formatMoney(context.amount)],
      ['Due Date', context.dueDate || 'N/A']
    ];
  }
  if (type === 'proposal') {
    return [
      ['Proposal #', context.proposalNumber || 'N/A'],
      ['Total', formatMoney(context.total)],
      ['Expires', context.expiresAt || 'N/A']
    ];
  }
  if (type === 'review-request') {
    return [
      ['Project', context.projectName || 'N/A'],
      ['Recipient', context.clientName || 'N/A']
    ];
  }
  return [];
}

function getPrimaryLink(type, context) {
  if (type === 'invoice') return context.paymentUrl || context.invoiceLink || '';
  if (type === 'proposal') return context.proposalLink || '';
  if (type === 'password-reset') return context.resetLink || '';
  if (type === 'review-request') return context.reviewLink || '';
  return context.inviteLink || '';
}

function buildEmailFromTemplate(templateKey, type, context, builder) {
  const selected = TEMPLATE_LIBRARY.find((x) => x.key === templateKey) || TEMPLATE_LIBRARY[0];
  const textColor = templateKey === 'midnight' ? '#e2e8f0' : '#1f2937';
  const panelBg = templateKey === 'midnight' ? '#111827' : '#ffffff';
  const borderColor = templateKey === 'midnight' ? '#1f2937' : '#e5e7eb';
  const softText = templateKey === 'midnight' ? '#94a3b8' : '#6b7280';
  const primaryLink = getPrimaryLink(type, context);
  const summaryRows = buildSummaryRows(type, context);

  const summaryHtml = summaryRows.length
    ? `<table style="width:100%; border-collapse: collapse; margin: 14px 0;">
        ${summaryRows.map(([label, value]) => `
          <tr>
            <td style="padding:8px 0; color:${softText};">${escapeHtml(label)}</td>
            <td style="padding:8px 0; text-align:right; color:${textColor}; font-weight:600;">${escapeHtml(value)}</td>
          </tr>`).join('')}
      </table>`
    : '';

  const secondaryButton = type === 'invoice' && builder.showSecondary && context.invoiceLink
    ? `<a href="${context.invoiceLink}" target="_blank" rel="noopener noreferrer" style="display:inline-block; margin-left:8px; padding:11px 16px; border:1px solid ${borderColor}; color:${textColor}; text-decoration:none; border-radius:8px; font-weight:600;">${escapeHtml(builder.secondaryCtaLabel || 'View Invoice')}</a>`
    : '';

  const proposalButtons = type === 'proposal' && builder.showResponseButtons
    ? `<div style="margin-top:12px;">
        ${context.acceptLink ? `<a href="${context.acceptLink}" style="display:inline-block; margin-right:8px; padding:10px 14px; border-radius:8px; text-decoration:none; background:#0284c7; color:#fff; font-weight:600;">${escapeHtml(builder.acceptLabel || 'Accept Proposal')}</a>` : ''}
        ${context.rejectLink ? `<a href="${context.rejectLink}" style="display:inline-block; padding:10px 14px; border-radius:8px; text-decoration:none; background:#dc2626; color:#fff; font-weight:600;">${escapeHtml(builder.rejectLabel || 'Decline Proposal')}</a>` : ''}
      </div>`
    : '';

  const topBar = templateKey === 'minimal'
    ? `<div style="padding:18px 22px; border-bottom:1px solid ${borderColor}; background:${selected.surface};">
         <h1 style="margin:0; font-size:22px; color:${textColor};">${escapeHtml(builder.headline || 'Update')}</h1>
       </div>`
    : `<div style="padding:20px 22px; border-radius:10px 10px 0 0; background:linear-gradient(120deg, ${selected.accent}, #06b6d4); color:white;">
         <h1 style="margin:0; font-size:22px;">${escapeHtml(builder.headline || 'Update')}</h1>
       </div>`;

  return `
    <div style="font-family:Arial,sans-serif; max-width:640px; margin:0 auto; color:${textColor};">
      <div style="background:${panelBg}; border:1px solid ${borderColor}; border-radius:10px; overflow:hidden;">
        ${topBar}
        <div style="padding:24px; background:${selected.surface};">
          <p style="margin:0 0 12px;">Hi <strong>${escapeHtml(builder.greetingName || context.clientName || 'there')}</strong>,</p>
          <p style="margin:0 0 14px; color:${softText}; line-height:1.5;">${escapeHtml(builder.intro || '')}</p>
          ${summaryHtml}
          ${primaryLink ? `<a href="${primaryLink}" target="_blank" rel="noopener noreferrer" style="display:inline-block; margin-top:8px; padding:11px 16px; border-radius:8px; text-decoration:none; background:${selected.accent}; color:white; font-weight:700;">${escapeHtml(builder.primaryCtaLabel || 'Open')}</a>` : ''}
          ${secondaryButton}
          ${proposalButtons}
          <p style="margin:18px 0 0; font-size:12px; color:${softText};">${escapeHtml(builder.footerNote || '')}</p>
        </div>
      </div>
    </div>
  `;
}

export default function EmailPreviewModal({ isOpen, onClose, onConfirm, clientName, clientEmail, isLoading = false, previewRequest = null }) {
  const [htmlContent, setHtmlContent] = useState('');
  const [editedContent, setEditedContent] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [error, setError] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [isAdvancedHtml, setIsAdvancedHtml] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('classic');
  const [previewContext, setPreviewContext] = useState({});
  const [builder, setBuilder] = useState(createBuilderDefaults('invite', {}, {}));

  const emailType = previewContext.type || previewRequest?.type || 'invite';

  const generatedHtml = useMemo(() => {
    return buildEmailFromTemplate(selectedTemplate, emailType, previewContext, builder);
  }, [selectedTemplate, emailType, previewContext, builder]);

  const handleLoadPreview = useCallback(async () => {
    setPreviewLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('adminToken');
      const apiUrl = process.env.REACT_APP_API_URL || 'https://api.pagehyve.com/api';
      const endpointUrl = `${apiUrl}/email/preview`;

      const requestBody = previewRequest ? { ...previewRequest } : {
        type: 'invite',
        clientName: clientName || 'Valued Client',
        inviteLink: `https://www.pagehyve.com/portal?invite=TOKEN_HERE&email=${encodeURIComponent(clientEmail || '')}`
      };

      const res = await fetch(endpointUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      if (!res.ok) {
        let errBody = null;
        try { errBody = await res.json(); } catch (e) { /* ignore */ }
        const message = errBody?.details || errBody?.error || `${res.status} ${res.statusText}`;
        throw new Error(`Failed to load preview: ${message}`);
      }

      const data = await res.json();
      const context = data.previewContext || { ...(previewRequest || {}), type: previewRequest?.type || 'invite' };
      setPreviewContext(context);
      setBuilder(createBuilderDefaults(context.type, context, previewRequest || {}));
      setHtmlContent(data.html || '');
      setEditedContent(data.html || '');
    } catch (err) {
      setError(err.message || 'Failed to load preview');
    } finally {
      setPreviewLoading(false);
    }
  }, [clientName, clientEmail, previewRequest]);

  useEffect(() => {
    if (isOpen) handleLoadPreview();
  }, [isOpen, handleLoadPreview]);

  useEffect(() => {
    if (isEditMode && !isAdvancedHtml && Object.keys(previewContext || {}).length) {
      setEditedContent(generatedHtml);
    }
  }, [isEditMode, isAdvancedHtml, generatedHtml, previewContext]);

  useEffect(() => {
    if (!isOpen) {
      setHtmlContent('');
      setEditedContent('');
      setPreviewLoading(false);
      setError('');
      setIsEditMode(false);
      setIsAdvancedHtml(false);
      setSelectedTemplate('classic');
      setPreviewContext({});
      setBuilder(createBuilderDefaults('invite', {}, {}));
    }
  }, [isOpen]);

  const handleConfirm = () => {
    onConfirm(editedContent && editedContent !== htmlContent ? editedContent : undefined);
  };

  if (!isOpen) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div style={{ background: 'white', borderRadius: '0.5rem', maxWidth: '980px', width: '100%', maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '1.2rem 1.5rem', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700 }}>Email Preview</h2>
          <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
            {htmlContent ? (
              <button
                onClick={() => setIsEditMode((v) => !v)}
                style={{ padding: '0.5rem 0.8rem', background: isEditMode ? '#0ea5e9' : '#e5e7eb', color: isEditMode ? 'white' : '#334155', border: 'none', borderRadius: '0.4rem', cursor: 'pointer', display: 'inline-flex', gap: '0.4rem', alignItems: 'center', fontWeight: 600 }}
              >
                {isEditMode ? <Eye size={16} /> : <Edit2 size={16} />}
                {isEditMode ? 'Preview' : 'Customize'}
              </button>
            ) : null}
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.4rem' }}>
              <X size={22} />
            </button>
          </div>
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: '1rem', background: '#f8fafc' }}>
          {error ? (
            <div style={{ background: '#fee2e2', color: '#991b1b', padding: '1rem', borderRadius: '0.5rem', marginBottom: '1rem', display: 'flex', gap: '0.75rem' }}>
              <AlertCircle size={20} style={{ flexShrink: 0 }} />
              <span>{error}</span>
            </div>
          ) : null}

          {previewLoading ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>Loading preview...</div>
          ) : isEditMode ? (
            <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: '1rem' }}>
              <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '0.6rem', padding: '0.9rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Template Library</div>
                {TEMPLATE_LIBRARY.map((template) => (
                  <button
                    key={template.key}
                    onClick={() => setSelectedTemplate(template.key)}
                    style={{
                      textAlign: 'left',
                      padding: '0.65rem 0.7rem',
                      borderRadius: '0.5rem',
                      border: selectedTemplate === template.key ? `2px solid ${template.accent}` : '1px solid #dbeafe',
                      background: '#fff',
                      cursor: 'pointer',
                      fontWeight: 600,
                      color: '#1e293b'
                    }}
                  >
                    {template.name}
                  </button>
                ))}

                <div style={{ marginTop: '0.2rem', borderTop: '1px solid #e2e8f0', paddingTop: '0.8rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.86rem', color: '#334155', fontWeight: 600 }}>
                    <input type="checkbox" checked={isAdvancedHtml} onChange={(e) => setIsAdvancedHtml(e.target.checked)} />
                    Advanced HTML mode
                    <Code2 size={14} />
                  </label>
                </div>

                {!isAdvancedHtml ? (
                  <>
                    <label style={{ fontSize: '0.82rem', color: '#475569' }}>
                      Greeting Name
                      <input value={builder.greetingName || ''} onChange={(e) => setBuilder((prev) => ({ ...prev, greetingName: e.target.value }))} style={{ width: '100%', marginTop: '0.3rem', padding: '0.5rem', borderRadius: '0.4rem', border: '1px solid #cbd5e1' }} />
                    </label>
                    <label style={{ fontSize: '0.82rem', color: '#475569' }}>
                      Headline
                      <input value={builder.headline || ''} onChange={(e) => setBuilder((prev) => ({ ...prev, headline: e.target.value }))} style={{ width: '100%', marginTop: '0.3rem', padding: '0.5rem', borderRadius: '0.4rem', border: '1px solid #cbd5e1' }} />
                    </label>
                    <label style={{ fontSize: '0.82rem', color: '#475569' }}>
                      Intro Text
                      <textarea rows="3" value={builder.intro || ''} onChange={(e) => setBuilder((prev) => ({ ...prev, intro: e.target.value }))} style={{ width: '100%', marginTop: '0.3rem', padding: '0.5rem', borderRadius: '0.4rem', border: '1px solid #cbd5e1', resize: 'vertical' }} />
                    </label>
                    <label style={{ fontSize: '0.82rem', color: '#475569' }}>
                      Primary Button Label
                      <input value={builder.primaryCtaLabel || ''} onChange={(e) => setBuilder((prev) => ({ ...prev, primaryCtaLabel: e.target.value }))} style={{ width: '100%', marginTop: '0.3rem', padding: '0.5rem', borderRadius: '0.4rem', border: '1px solid #cbd5e1' }} />
                    </label>

                    {emailType === 'proposal' ? (
                      <>
                        <label style={{ fontSize: '0.82rem', color: '#475569' }}>
                          <input type="checkbox" checked={Boolean(builder.showResponseButtons)} onChange={(e) => setBuilder((prev) => ({ ...prev, showResponseButtons: e.target.checked }))} style={{ marginRight: '0.45rem' }} />
                          Show Accept/Decline buttons
                        </label>
                        <label style={{ fontSize: '0.82rem', color: '#475569' }}>
                          Accept Label
                          <input value={builder.acceptLabel || ''} onChange={(e) => setBuilder((prev) => ({ ...prev, acceptLabel: e.target.value }))} style={{ width: '100%', marginTop: '0.3rem', padding: '0.5rem', borderRadius: '0.4rem', border: '1px solid #cbd5e1' }} />
                        </label>
                        <label style={{ fontSize: '0.82rem', color: '#475569' }}>
                          Decline Label
                          <input value={builder.rejectLabel || ''} onChange={(e) => setBuilder((prev) => ({ ...prev, rejectLabel: e.target.value }))} style={{ width: '100%', marginTop: '0.3rem', padding: '0.5rem', borderRadius: '0.4rem', border: '1px solid #cbd5e1' }} />
                        </label>
                      </>
                    ) : null}

                    <label style={{ fontSize: '0.82rem', color: '#475569' }}>
                      Footer Note
                      <textarea rows="2" value={builder.footerNote || ''} onChange={(e) => setBuilder((prev) => ({ ...prev, footerNote: e.target.value }))} style={{ width: '100%', marginTop: '0.3rem', padding: '0.5rem', borderRadius: '0.4rem', border: '1px solid #cbd5e1', resize: 'vertical' }} />
                    </label>
                  </>
                ) : (
                  <label style={{ fontSize: '0.82rem', color: '#475569' }}>
                    HTML Content
                    <textarea
                      value={editedContent}
                      onChange={(e) => setEditedContent(e.target.value)}
                      rows="18"
                      style={{ width: '100%', marginTop: '0.35rem', padding: '0.6rem', borderRadius: '0.4rem', border: '1px solid #cbd5e1', fontFamily: 'Consolas, Monaco, monospace', fontSize: '0.8rem', resize: 'vertical' }}
                    />
                  </label>
                )}
              </div>

              <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '0.6rem', padding: '0.7rem' }}>
                <iframe srcDoc={editedContent} title="Email preview" style={{ width: '100%', height: '560px', border: 'none', borderRadius: '0.4rem' }} />
              </div>
            </div>
          ) : htmlContent ? (
            <div style={{ background: '#ffffff', borderRadius: '0.5rem', padding: '0.75rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
              <iframe srcDoc={editedContent || htmlContent} style={{ width: '100%', height: '560px', border: 'none', borderRadius: '0.3rem' }} title="Email preview" />
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>No preview available</div>
          )}
        </div>

        <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #e5e7eb', display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{ padding: '0.5rem 0.95rem', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '0.375rem', cursor: 'pointer', fontWeight: '500' }}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            style={{ padding: '0.5rem 1rem', background: 'linear-gradient(to right, #10b981, #06b6d4)', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.45rem' }}
            disabled={isLoading || previewLoading}
          >
            <Send size={17} />
            {isLoading ? 'Sending...' : 'Send Email'}
          </button>
        </div>
      </div>
    </div>
  );
}
